import {useLocalStorageValue} from '@react-hookz/web';
import {extractClipboardData} from 'Common/extractClipboardData';
import {useCallback, useEffect} from 'react';
import {Project} from 'rendition-core';
import postcss from 'postcss';
import postcssJs from 'postcss-js';
import {ERROR, useAppStore} from 'stores/app';

export interface StyleGuide {
    cssClasses: Project['cssClasses'];
    cssClassMeta: Project['cssClassMeta'];
}

type ClipboardDataCallback = (data: DataTransfer | null) => void;
type ClipboardItemsCallback = (data: ClipboardItems) => void;

interface WebflowJSON {
    type: string;
    payload: {
        styles: [
            {
                _id: string;
                name: string;
                styleLess: string;
                createdBy: string;
                variants: string;
                selector: string | null;
            }
        ];
    };
}

export const useListenForPaste = (cb: ClipboardDataCallback) => {
    useEffect(() => {
        const pasteHandler = (e: ClipboardEvent) => {
            cb(e.clipboardData);
        };

        document.addEventListener('paste', pasteHandler);
        return () => {
            document.removeEventListener('paste', pasteHandler);
        };
    }, [cb]);
};

export const usePasteButton = (cb: ClipboardItemsCallback) => {
    const clipboardEnabled = !!navigator.clipboard?.read;
    const pasteFromClipboard = useCallback(() => {
        navigator.clipboard.read().then((data) => {
            cb(data);
        });
    }, [cb]);

    return {pasteFromClipboard, clipboardEnabled};
};

export const useStyleguideData = (existingStyleGuide: StyleGuide | undefined, classKey: string) => {
    const {value, set, remove} = useLocalStorageValue<StyleGuide>('rendition-styleguide');
    const setToast = useAppStore.use.setToast();

    useEffect(() => {
        if (existingStyleGuide) {
            set(existingStyleGuide);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingStyleGuide]);

    const extractAndSetData = useCallback(
        async (d: ClipboardItems | DataTransfer | null) => {
            try {
                const extractedData = d
                    ? await Promise.all((Array.isArray(d) ? d : [d]).map(extractClipboardData))
                    : [];

                const [data] = extractedData;
                if (data) {
                    const webflowData = JSON.parse(data) as WebflowJSON;
                    if (webflowData.type !== '@webflow/XscpData') {
                        return setToast({type: ERROR, message: 'Could not parse Webflow JSON', open: true});
                    }
                    const styleString = webflowData.payload.styles[0]?.styleLess || '';

                    if (styleString.length === 0) {
                        return setToast({
                            type: ERROR,
                            message: 'No styles found, ensure your class is set properly in Webflow',
                            open: true,
                        });
                    }

                    const styles = postcss.parse(styleString);
                    const stylesObject: {[cssAttribute: string]: string} = postcssJs.objectify(styles);

                    const webflowStyles = webflowData.payload.styles[0];
                    return set({
                        ...value,
                        cssClasses: {
                            ...value?.cssClasses,
                            [classKey]: stylesObject,
                        },
                        cssClassMeta: {
                            ...value?.cssClassMeta,
                            [classKey]: {
                                webflow: {
                                    id: webflowStyles?._id,
                                    name: webflowStyles?.name,
                                    variants: JSON.stringify(webflowStyles?.variants),
                                    styleString: styleString,
                                    createdBy: webflowStyles?.createdBy,
                                },
                            },
                        },
                    });
                }
                return setToast({
                    type: ERROR,
                    message: 'No data found in clipboard',
                    open: true,
                });
            } catch (e) {
                console.error(e);
                return setToast({
                    type: ERROR,
                    message: 'Something went wrong parsing clipboard data',
                    open: true,
                });
            }
        },
        [classKey, set, value, setToast]
    );

    const {pasteFromClipboard, clipboardEnabled} = usePasteButton(extractAndSetData);
    useListenForPaste(extractAndSetData);

    return {
        value,
        set,
        remove,
        pasteFromClipboard,
        clipboardEnabled,
    };
};
